import React, { useState, useCallback } from 'react';

// Helper component for the checkmark icon
const CheckIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

// The main reusable component
interface ClickToCopyDivProps {
  /** The text that will be copied to the clipboard when the div is clicked. */
  textToCopy: string;
  /** The content to be displayed inside the clickable div. */
  children: React.ReactNode;
  /** Optional additional class names for styling the container div. */
  className?: string;
}

const ClickToCopyDiv: React.FC<ClickToCopyDivProps> = ({ textToCopy, children, className = '' }) => {
  const [isCopied, setIsCopied] = useState(false);

  /**
   * Handles the copy-to-clipboard logic.
   * Uses document.execCommand as a fallback for environments where navigator.clipboard might be restricted (like some iframes).
   */
  const handleCopy = useCallback(() => {
    const textArea = document.createElement('textarea');
    textArea.value = textToCopy;
    // Make the textarea non-editable and invisible
    textArea.style.position = 'fixed';
    textArea.style.top = '-9999px';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      // Execute the copy command
      document.execCommand('copy');
      setIsCopied(true);

      // Reset the copied state after a short delay
      setTimeout(() => {
        setIsCopied(false);
      }, 2000); // Tooltip will be visible for 2 seconds
    } catch (err) {
      console.error('Failed to copy text to clipboard: ', err);
    } finally {
      // Clean up the temporary textarea
      document.body.removeChild(textArea);
    }
  }, [textToCopy]);

  return (
    <div
      onClick={handleCopy}
      className={`relative cursor-pointer ${className}`}
      aria-label={`Click to copy: ${textToCopy}`}
      tabIndex={0} // Make it focusable
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleCopy()}
    >
      {/* The main content passed to the component */}
      {children}

      {/* Animated Tooltip */}
      <div
        className={`
          absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full
          flex items-center gap-2
          bg-gray-800 text-white
          px-4 py-2 rounded-md shadow-lg
          transition-all duration-300 ease-in-out
          pointer-events-none
          ${isCopied ? 'opacity-100 translate-y-[-115%]' : 'opacity-0'}
        `}
        role="alert"
        aria-live="polite"
      >
        <CheckIcon className="w-5 h-5 text-green-400" />
        <span>Copied!</span>
      </div>
    </div>
  );
};

export default ClickToCopyDiv;